/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import PromptCopiedModal from "../components/dashboard/PromptCopiedModal";

export default function BusinessProfile() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const isFree = (session?.user as any)?.highest_tier_level === 0;
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [copiedDescription, setCopiedDescription] = useState("");

  const copyToClipboard = (text: string, description: string) => {
    navigator.clipboard.writeText(text);
    setCopiedDescription(description);
    setIsPromptModalOpen(true);
  };

  const userId = session?.user?.id;

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    }
  }, [status, navigate]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const res = await api.get(`/auth/status?userId=${userId}`);
        if (res.data) {
          setProfile(res.data.profile);
        }
      } catch (err) {
        console.error("Failed to fetch business profile:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  if (status === "loading" || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <p className="text-xs font-black uppercase tracking-widest text-slate-500">Retrieving Strategy...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto text-center space-y-8 py-20 animate-in fade-in duration-500">
        <div className="w-24 h-24 bg-surface-container-high rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="material-symbols-outlined text-4xl text-slate-400">business_center</span>
        </div>
        <h2 className="text-3xl font-black tracking-tighter text-on-surface">No Business Profile Found</h2>
        <p className="text-on-surface-variant max-w-md mx-auto">
          You haven't activated your business profile yet. Complete the activation process to generate your AI strategy.
        </p>
        <button 
          onClick={() => navigate("/activation")}
          className="px-8 py-4 bg-primary text-surface rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
        >
          Start Activation
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      {/* Header & Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 px-2">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
              <span className="material-symbols-outlined font-black">business_center</span>
            </div>
            <div>
              <h2 className="text-3xl font-black text-on-surface tracking-tighter leading-tight">
                {profile.product_service_name || profile.business_name || "My Business"}
              </h2>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-500">
                  {profile.category || "General"} • {profile.location || "Online"}
                </p>
                <span className="text-slate-300">•</span>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Activated: {new Date(profile.created_at || profile.createdAt || Date.now()).toLocaleDateString()}
                </p>
              </div>

              {/* Dynamic Strategy & Language Badges with Breathing Room */}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {profile.selected_strategy && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-500/10">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {profile.selected_strategy === 'viral_poster' ? 'Viral Strategy' : profile.selected_strategy === 'brand_poster' ? 'Brand Strategy' : 'Offer Strategy'}
                  </span>
                )}
                {profile.selected_language && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/10 text-blue-700 dark:text-blue-400 rounded-lg text-[10px] font-black uppercase tracking-widest border border-blue-500/10">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    {profile.selected_language === 'simple_english' ? 'Simple English' : profile.selected_language === 'manglish' ? 'Manglish' : 'Malayalam'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => navigate(`/activation?profileId=${profile.id}&reinvent=true`)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-primary text-surface rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-105 active:scale-95 transition-all"
          >
            <span className="material-symbols-outlined text-lg">edit</span>
            Edit Profile
          </button>
        </div>
      </div>

      {/* Business Context Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
         {/* Key Features (Spans 2) */}
         <div className="md:col-span-2 bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-center gap-3 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12 blur-2xl group-hover:bg-primary/10 transition-colors" />
            <div className="flex items-center gap-3 relative z-10">
              <span className="material-symbols-outlined text-primary text-sm">auto_fix_high</span>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Strategic Features</h3>
            </div>
            <p className="text-sm font-bold text-on-surface-variant leading-relaxed relative z-10">
              {profile.key_features}
            </p>
         </div>

         {/* Contact Info */}
         <div className="bg-surface-container-low border border-outline-variant/10 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-center gap-3 group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-50 shrink-0">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-emerald-500" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">WhatsApp Support</h3>
            </div>
            <p className="text-lg font-black text-on-surface group-hover:text-emerald-600 transition-colors tracking-tight">
              {profile.whatsapp}
            </p>
         </div>

         {/* Pricing/Status */}
         <div className="bg-primary/5 border border-primary/10 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-center gap-3 group relative overflow-hidden">
            <div className="absolute inset-0 bg-primary/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                <span className="material-symbols-outlined text-sm">payments</span>
              </div>
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/60">Product Price</h3>
            </div>
            <p className="text-lg font-black text-primary relative z-10 tracking-tight">{profile.price_range || "Market Standard"}</p>
         </div>
      </div>

      <div className="space-y-12">
        
        {/* Full Width Strategic Assets */}
        {!isFree && (
          <div id="tour-intelligence-assets" className="space-y-8">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-2xl font-black tracking-tighter text-on-surface">Intelligence Assets</h3>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Selected Strategy</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Target Audiences Section */}
            <div className="col-span-1 md:col-span-2 space-y-4">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-500/10 rounded-lg flex items-center justify-center text-indigo-500">
                    <span className="material-symbols-outlined text-sm">groups</span>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Market Segments</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(profile.target_audiences?.audiences || []).map((audience: any, idx: number) => {
                  const isSelected = audience.audience_name === profile.selected_audience?.audience_name;
                  return (
                    <div 
                      key={idx}
                      className={`p-6 rounded-[2.5rem] border-2 transition-all flex flex-col gap-5 relative overflow-hidden ${
                        isSelected 
                          ? "bg-[#067a52] text-white border-transparent shadow-2xl shadow-emerald-900/40" 
                          : "border-outline-variant/10 bg-surface-container-lowest opacity-50 grayscale-[0.8] hover:opacity-100 hover:grayscale-0"
                      }`}
                    >
                      {isSelected && (
                        <>
                          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                          <div className="absolute bottom-10 right-4 w-12 h-12 bg-white/5 rounded-xl rotate-12" />
                          
                          <div className="flex items-center gap-2 relative z-10">
                            <div className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded text-[8px] font-black uppercase tracking-widest">Selected</div>
                            <div className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-200">
                              <span className="material-symbols-outlined text-[10px]">verified</span>
                              Active Strategy
                            </div>
                          </div>
                        </>
                      )}

                      <div className="space-y-2 relative z-10">
                        <h4 className={`text-xl font-black tracking-tight leading-tight ${isSelected ? "text-white" : "text-on-surface"}`}>
                          {audience.audience_name}
                        </h4>
                        <p className={`text-xs font-bold leading-relaxed ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>
                          {audience.identity || audience.who_they_are}
                        </p>
                      </div>

                      <div className={`pt-4 border-t relative z-10 ${isSelected ? "border-white/10" : "border-outline-variant/10"} mt-auto`}>
                        <p className={`text-[8px] font-black uppercase tracking-widest mb-1.5 ${isSelected ? "text-emerald-300/60" : "text-slate-400"}`}>Core Desire</p>
                        <p className={`text-sm font-bold italic leading-tight ${isSelected ? "text-white" : "text-slate-500"}`}>
                          &quot;{audience.dream_state || audience.main_desire}&quot;
                        </p>
                        {audience.dual_struggle && (
                           <p className={`text-xs font-medium mt-2 line-clamp-2 ${isSelected ? "text-emerald-100/80" : "text-slate-500/80"}`}>
                              {audience.dual_struggle.emotional}
                           </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Positioning Section */}
            <div className="col-span-1 md:col-span-2 space-y-4">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-sky-500/10 rounded-lg flex items-center justify-center text-sky-500">
                    <span className="material-symbols-outlined text-sm">rocket_launch</span>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Strategic Positioning</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(profile.positioning_options?.positioning_options || []).map((option: any, idx: number) => {
                  const isSelected = (option.niche_name || option.angle) === (profile.selected_positioning?.niche_name || profile.selected_positioning?.angle);
                  return (
                    <div 
                      key={idx}
                      className={`p-6 rounded-[2.5rem] border-2 transition-all flex flex-col gap-5 relative overflow-hidden ${
                        isSelected 
                          ? "bg-[#067a52] text-white border-transparent shadow-2xl shadow-emerald-900/40" 
                          : "border-outline-variant/10 bg-surface-container-lowest opacity-50 grayscale-[0.8] hover:opacity-100 hover:grayscale-0"
                      }`}
                    >
                      {isSelected && (
                        <>
                          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                          <div className="absolute bottom-10 right-4 w-12 h-12 bg-white/5 rounded-xl rotate-12" />
                          
                          <div className="flex items-center gap-2 relative z-10">
                            <div className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded text-[8px] font-black uppercase tracking-widest">Selected</div>
                            <div className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-200">
                              <span className="material-symbols-outlined text-[10px]">verified</span>
                              Active Angle
                            </div>
                          </div>
                        </>
                      )}

                      <div className="space-y-2 relative z-10">
                        <h4 className={`text-xl font-black tracking-tight leading-tight ${isSelected ? "text-white" : "text-on-surface"}`}>
                          {option.niche_name || option.angle}
                        </h4>
                        <p className={`text-xs font-bold leading-relaxed ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>
                          &quot;{option.positioning_hook || option.positioning_statement}&quot;
                        </p>
                      </div>

                      <div className={`pt-4 border-t relative z-10 ${isSelected ? "border-white/10" : "border-outline-variant/10"} mt-auto`}>
                        <p className={`text-[8px] font-black uppercase tracking-widest mb-2 ${isSelected ? "text-emerald-300/60" : "text-slate-400"}`}>Strategy Logic</p>
                        <p className={`text-[11px] font-bold leading-tight ${isSelected ? "text-white" : "text-slate-500"}`}>
                          {option.why_it_wins || option.the_niche_logic || (option.pain_points && option.pain_points.join(", "))}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ad Creative Copy Section */}
            <div className="col-span-1 md:col-span-2 space-y-4">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-pink-500/10 rounded-lg flex items-center justify-center text-pink-500">
                    <span className="material-symbols-outlined text-sm">draw</span>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Ad Creative Copy</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(profile.ad_captions?.conversion_angles || profile.ad_captions?.ad_captions || []).map((caption: any, idx: number) => {
                  const isSelected = (caption.poster_headline || caption.hook) === (profile.selected_caption?.poster_headline || profile.selected_caption?.hook);
                  return (
                    <div 
                      key={idx}
                      className={`p-6 rounded-[2.5rem] border-2 transition-all flex flex-col gap-5 relative overflow-hidden ${
                        isSelected 
                          ? "bg-[#067a52] text-white border-transparent shadow-2xl shadow-emerald-900/40" 
                          : "border-outline-variant/10 bg-surface-container-lowest opacity-50 grayscale-[0.8] hover:opacity-100 hover:grayscale-0"
                      }`}
                    >
                      {isSelected && (
                        <>
                          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                          <div className="absolute bottom-10 right-4 w-12 h-12 bg-white/5 rounded-xl rotate-12" />
                          
                          <div className="flex items-center gap-2 relative z-10">
                            <div className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded text-[8px] font-black uppercase tracking-widest">Selected</div>
                            <div className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-200">
                              <span className="material-symbols-outlined text-[10px]">verified</span>
                              Active Ad
                            </div>
                          </div>
                        </>
                      )}

                      <div className="space-y-4 relative z-10">
                        <div className="space-y-1">
                            <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-primary"}`}>Headline / Hook</p>
                            <h4 className={`text-base font-black tracking-tight leading-tight italic ${isSelected ? "text-white" : "text-on-surface"}`}>
                              &quot;{caption.poster_headline || caption.hook}&quot;
                            </h4>
                        </div>
                        
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-slate-400"}`}>Angle Type</p>
                                <p className={`text-[11px] font-bold leading-relaxed ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>
                                  {caption.angle_type || "Result-Oriented"}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <p className={`text-[8px] font-black uppercase tracking-widest ${isSelected ? "text-emerald-300/60" : "text-emerald-500"}`}>Emotional Logic</p>
                                <p className={`text-[11px] font-bold leading-relaxed ${isSelected ? "text-emerald-50/80" : "text-on-surface-variant"}`}>
                                  {caption.emotional_logic || caption.solution}
                                </p>
                            </div>
                        </div>
                      </div>

                      <div className={`mt-auto p-4 rounded-2xl border relative z-10 ${isSelected ? "bg-white/10 border-white/10" : "bg-primary/5 border-primary/10"}`}>
                        <p className={`text-[8px] font-black uppercase tracking-widest mb-1 ${isSelected ? "text-emerald-300/60" : "text-primary"}`}>CTA / WhatsApp Caption</p>
                        <p className={`text-[11px] font-black ${isSelected ? "text-white" : "text-on-surface"}`}>
                            {caption.tempt_to_buy_cta || caption.cta || caption.whatsapp_status_caption}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AI Poster Prompts Section */}
            <div className="col-span-1 md:col-span-2 space-y-6">
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-xl">auto_awesome</span>
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">Poster Generation Prompts</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-8">
                {(Array.isArray(profile.ai_poster_prompts?.poster_prompts) 
                    ? profile.ai_poster_prompts?.poster_prompts 
                    : (profile.ai_poster_prompts?.design_package || profile.ai_poster_prompts) 
                      ? [profile.ai_poster_prompts?.design_package || profile.ai_poster_prompts] 
                      : []
                ).map((prompt: any, idx: number) => (
                  <div 
                    key={idx}
                    className="bg-slate-900 border-2 border-slate-800 p-8 md:p-12 rounded-[3.5rem] shadow-2xl flex flex-col gap-10 relative overflow-hidden group/card transition-all hover:border-primary/30 w-full"
                  >
                    {/* Decorative Gradient */}
                    <div className="absolute -top-24 -right-24 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none opacity-50" />
                    
                    <div className="flex flex-col gap-10 relative z-10">
                      {/* Top Row: Concept Logic */}
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">The Vision</span>
                          </div>
                          {prompt.pillar && (
                            <div 
                              className="px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all duration-300 shadow-lg shadow-black/20"
                              style={
                                prompt.pillar.includes("DREAM")
                                  ? { backgroundColor: "rgba(56, 189, 248, 0.2)", borderColor: "rgba(56, 189, 248, 0.5)", color: "#38bdf8" }
                                  : prompt.pillar.includes("PAIN")
                                  ? { backgroundColor: "rgba(251, 113, 133, 0.2)", borderColor: "rgba(251, 113, 133, 0.5)", color: "#fb7185" }
                                  : prompt.pillar.includes("TRANSFORMATION")
                                  ? { backgroundColor: "rgba(167, 139, 250, 0.2)", borderColor: "rgba(167, 139, 250, 0.5)", color: "#a78bfa" }
                                  : { backgroundColor: "rgba(52, 211, 153, 0.2)", borderColor: "rgba(52, 211, 153, 0.5)", color: "#34d399" }
                              }
                            >
                              {prompt.pillar.replace("THE ", "")}
                            </div>
                          )}
                        </div>
                        <h4 className="text-xl md:text-2xl font-bold text-slate-200 tracking-tight leading-relaxed max-w-5xl italic">
                          {prompt.description || prompt.concept_name || "Strategic Visual Concept"}
                        </h4>

                        <div className="p-4 bg-white/5 rounded-2xl border border-white/5 max-w-fit mt-4">
                          <p className="text-[10px] font-bold text-slate-400 leading-relaxed flex items-center gap-3">
                            <span className="material-symbols-outlined text-primary text-xs">auto_awesome</span>
                            Synthesized Strategy for {profile.product_service_name}
                          </p>
                        </div>
                      </div>

                      {/* Bottom Row: AI Prompt Editor */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between px-2">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 italic">Ready-to-Use Visual Code</span>
                          </div>
                        </div>

                        <div className="relative group/prompt overflow-hidden rounded-[2.5rem] border border-white/5 bg-black/40 shadow-2xl">
                          <div className="p-8 md:p-12 min-h-[160px] flex items-center">
                            <p className="text-[14px] md:text-base font-medium text-slate-300 leading-relaxed font-mono selection:bg-primary/40">
                              {prompt.pro_prompt || prompt.visual_ai_prompt || prompt.ai_prompt}
                            </p>
                          </div>

                          {/* Always Visible Copy Bar */}
                          <div className="bg-white/5 border-t border-white/5 p-4 flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(prompt.pro_prompt || prompt.visual_ai_prompt || prompt.ai_prompt, prompt.description || prompt.concept_name || "Strategic Visual Concept");
                              }}
                              className="px-8 py-4 bg-primary text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all flex items-center gap-3 shadow-xl"
                            >
                              <span className="material-symbols-outlined text-base">content_copy</span>
                              Copy Master Prompt
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
        )}

      </div>
      <PromptCopiedModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        description={copiedDescription}
      />
    </div>
  );
}
